import { Alert, AlertDescription, AlertTitle, Button } from '@1trading/react';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { MM_PROGRAMS_LIST_QUERY_KEY, mmProgramHistoryQueryKey, mmProgramQueryKey } from '@/hooks/queries/mm-programs/use-mm-programs';
import { getErrorMessage, HTTP_CONFLICT, isHttpStatus } from '@/lib/api/error';
import { type GetMmProgramResponse, updateMmProgram } from '@/services/api/mm-programs';
import { pluralize } from '@/utils/string';
import { type MarketMaker, marketMakerSchema } from '../mm-program-schema';
import { describeSettingsChanges } from '../settings-changes';
import { SettingsChangesList } from '../settings-changes-list';
import type { GridDocument } from './grid-rows';

export interface SaveFailure {
  pid: string;
  name: string;
  message: string;
  /** Someone saved since it was loaded; only a reload can resolve it */
  isConflict: boolean;
}

const CONFLICT_MESSAGE = 'Someone saved this market maker after it was loaded. Reload it to see their changes; reloading drops your edits to it.';

/**
 * Confirms every edited market maker's changes under one comment, then saves them one by one against the version each
 * was loaded at. A failure leaves that market maker's edits in place and does not stop the others.
 */
export const reviewAndSave = async (edited: GridDocument[], queryClient: QueryClient): Promise<SaveFailure[]> => {
  const prepared = edited.flatMap((document) => {
    const parsed = marketMakerSchema.safeParse(document.draft);
    return parsed.success ? [{ document, marketMaker: parsed.data as MarketMaker, changes: describeSettingsChanges(document.saved, parsed.data) }] : [];
  });
  if (prepared.length < edited.length) {
    toast.error('Fix the problems marked in the grid before saving.');
    return [];
  }
  const failures: SaveFailure[] = [];
  const confirmed = await confirmAction({
    title: `Save ${pluralize(prepared.length, 'market maker')}`,
    size: 'wide',
    actionLabel: 'Save all',
    description: (
      <div className="flex flex-col gap-5">
        {prepared.map(({ document, changes }) => (
          <section className="flex flex-col gap-2" key={document.pid}>
            <h3 className="font-semibold text-foreground">{document.saved.name}</h3>
            <SettingsChangesList groups={changes} />
          </section>
        ))}
      </div>
    ),
    onConfirm: async (comment) => {
      for (const { document, marketMaker } of prepared) {
        try {
          const response = await updateMmProgram(document.pid, { comment: comment.trim(), expectedVersion: document.version, marketMaker });
          queryClient.setQueryData<GetMmProgramResponse>(mmProgramQueryKey(document.pid), {
            marketMaker: response.marketMaker,
            version: response.version,
            canEdit: document.canEdit,
          });
          void queryClient.invalidateQueries({ queryKey: mmProgramHistoryQueryKey(document.pid) });
        } catch (error) {
          const isConflict = isHttpStatus(error, HTTP_CONFLICT);
          failures.push({ pid: document.pid, name: document.saved.name, isConflict, message: isConflict ? CONFLICT_MESSAGE : getErrorMessage(error) });
        }
      }
      void queryClient.invalidateQueries({ queryKey: MM_PROGRAMS_LIST_QUERY_KEY });
    },
  });
  if (confirmed === null) return [];
  const saved = prepared.length - failures.length;
  if (saved) toast.success(`Saved ${pluralize(saved, 'market maker')}`);
  if (failures.length) toast.error(`${pluralize(failures.length, 'market maker')} could not be saved. Their edits are kept.`);
  return failures;
};

/** Market makers a save did not reach, with a reload for the ones someone else saved meanwhile. */
export const SaveFailuresAlert = ({ failures, onReload }: { failures: SaveFailure[]; onReload: (pid: string) => void }) =>
  failures.length ? (
    <Alert variant="destructive">
      <AlertTitle>Not saved</AlertTitle>
      <AlertDescription>
        <ul className="flex flex-col gap-2 text-sm">
          {failures.map((failure) => (
            <li className="flex flex-wrap items-center gap-2" key={failure.pid}>
              <span>
                <span className="font-medium">{failure.name}</span>: {failure.message}
              </span>
              {failure.isConflict ? (
                <Button onClick={() => onReload(failure.pid)} size="sm" type="button" variant="outline">
                  Reload {failure.name}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  ) : null;

interface UnsavedEditsBarProps {
  /** The edited market makers' names; the bar shows only while there are any */
  names: string[];
  problems: number;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}

const UnsavedEditsPanel = ({ names, problems, isSaving, onDiscard, onSave }: UnsavedEditsBarProps) => {
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  return (
    <div className="sticky bottom-0 z-30 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-3 shadow-lg">
      <span className="text-sm">
        Unsaved edits to {pluralize(names.length, 'market maker')}: {names.join(', ')}
        {problems ? <span className="text-red-500"> · {pluralize(problems, 'problem')} to fix</span> : null}
      </span>
      <div className="flex gap-2">
        <Button onClick={onDiscard} type="button" variant="outline">
          Discard all
        </Button>
        <Button disabled={problems > 0 || isSaving} onClick={onSave} type="button" variant="primary">
          Review and save
        </Button>
      </div>
    </div>
  );
};
UnsavedEditsPanel.displayName = 'UnsavedEditsPanel';

/** Unsaved edits across market makers, shared by every view that edits drafts; leaving the page warns while it shows. */
export const UnsavedEditsBar = (props: UnsavedEditsBarProps) => (props.names.length ? <UnsavedEditsPanel {...props} /> : null);
